import { useParams } from 'react-router-dom';

import { getProjectPath, getProjectsBasePath } from '@verantos-dev/core-constants';
import { useGetProjectQuery } from '@verantos-dev/data-access-projects';
import type { PageHeaderBreadcrumb } from '@verantos-dev/shared-components';

export interface UseProjectRootBreadcrumbsResult {
  /** Leading `Project home` crumb shared by every sub-page. */
  rootCrumbs: PageHeaderBreadcrumb[];
  /** Resolved project name (undefined while loading). */
  projectName: string | undefined;
  /** Whether the project name query is still in flight. */
  isProjectNameLoading: boolean;
}

/**
 * Shared leading breadcrumbs for project sub-pages (OR-6123 F8).
 *
 * Every detail page's breadcrumb starts with `Project home`; this hook
 * resolves the project name and returns that crumb so the per-page hooks
 * only have to append their own trailing crumbs. Replaces the project-name +
 * "Projects" arms of the old monolithic `useProjectBreadcrumbs`.
 *
 * The crumb links back to the project home so a deep view always
 * has a one-click route back to the artifact list.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const useProjectRootBreadcrumbs = (): UseProjectRootBreadcrumbsResult => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { data, isLoading } = useGetProjectQuery(projectId, { skip: !projectId });
  const projectName = data?.data.projectName;
  const isProjectNameLoading = isLoading && !projectName;

  const rootCrumbs: PageHeaderBreadcrumb[] = [
    {
      label: 'Project home',
      href: projectId ? getProjectPath(projectId) : getProjectsBasePath(),
    },
  ];

  return { rootCrumbs, projectName, isProjectNameLoading };
};
